import type { ITreeNode } from '@platform/core/types';
import type { IPathRow } from './IPathRow';

/**
 * Дерево шляхів у порядку показу (прототип v4 areas/iterations, чиста функція): корінь, далі діти за `sort` і назвою в глибину; колір —
 * власний або найближчого предка («успадковано»; корінь без кольору — без позначки).
 */
export function pathRows(nodes: readonly ITreeNode[]): IPathRow[] {
  const children = new Map<string | null, ITreeNode[]>();
  for (const n of nodes) {
    const key = n.parentId && nodes.some((p) => p.id === n.parentId) ? n.parentId : null;
    children.set(key, [...(children.get(key) ?? []), n]);
  }
  const out: IPathRow[] = [];
  const walk = (parent: string | null, depth: number, color: string | null) => {
    const list = [...(children.get(parent) ?? [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || a.name.localeCompare(b.name));
    for (const n of list) {
      const own = n.color ?? null;
      out.push({ node: n, depth, color: own ?? color, inherited: !own && !!color });
      walk(n.id, depth + 1, own ?? color);
    }
  };
  walk(null, 0, null);
  return out;
}

/** Кількість записів у вузлі разом із нащадками: прямі записи вузла (за ідентифікатором) плюс записи всіх нащадків. Чиста функція. */
export function subtreeCounts(nodes: readonly ITreeNode[], direct: ReadonlyMap<string, number>): Map<string, number> {
  const total = new Map<string, number>();
  const count = (id: string): number => {
    if (total.has(id)) return total.get(id)!;
    const sum = (direct.get(id) ?? 0) + nodes.filter((n) => n.parentId === id).reduce((s, n) => s + count(n.id), 0);
    total.set(id, sum);
    return sum;
  };
  nodes.forEach((n) => count(n.id));
  return total;
}
